import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Input, TextArea } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import { domainsFrom, mailFormChanged } from '../lib/mailServer'
import type { MailServerDraft, MailServerSettings } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Mark, Masked, TestResult } from './connectParts'
import { Toggle, useFlash } from './sectionKit'
import { EDIT, InnerPage, Notice, PageHead, Seg, SettingBlock, SettingRow, SettingsCard, TagList, useOpenRecord, Values } from './settingsKit'
import '../styles/connect.css'

/**
 * Mail server: where an organisation's letters leave from. The installation
 * owner's alone (tenant.manage), and in English as the owner's other screens are.
 *
 * The page shows what is saved; Edit, in the page head, opens the form in a dialog. The password is
 * write-only: the form never shows it and sends one only when typed. The test
 * sends a real letter with what is saved.
 */

const BLANK: MailServerDraft = {
  host: '', port: 587, security: 'starttls', username: '', fromAddress: '', caPem: '', allowedDomains: [], enabled: true,
}

const draftOf = (m: MailServerSettings): MailServerDraft => ({
  host: m.host, port: m.port, security: m.security, username: m.username ?? '', fromAddress: m.fromAddress,
  caPem: m.caPem ?? '', allowedDomains: m.allowedDomains ?? [], enabled: m.enabled,
})

const SECURITY = [
  { key: 'starttls', name: 'STARTTLS', port: 587 },
  { key: 'tls', name: 'TLS', port: 465 },
  { key: 'none', name: 'None', port: 25 },
] as const

const refusal = (err: unknown) => (err instanceof ApiError ? err.message : 'The server could not be reached.')

export function Mail({ organisation }: { organisation?: string }) {
  const { say } = useStaffText()
  const { flash, toast } = useFlash()
  const [saved, setSaved] = useState<MailServerSettings | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  // The mail server's form is a page of its own: #mail/edit.
  const rec = useOpenRecord([], () => '', loaded, [EDIT])
  const editing = rec.code === EDIT && loaded

  const load = useCallback(async () => {
    try {
      const { mailServer } = await api.mailServer(organisation)
      setSaved(mailServer)
      setLoaded(true)
    } catch (err) {
      setError(refusal(err))
    }
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const sec = SECURITY.find((s) => s.key === saved?.security)
  const head = (
    <PageHead
      title={say('admin.section.mail')}
      line="Where your organisation's letters leave from."
      actions={
        loaded && (
          <Button variant={saved ? 'secondary' : 'primary'} onPress={() => rec.nav.open(EDIT)}>
            {saved ? (
              <>
                <Icon name="edit" size={15} /> Edit
              </>
            ) : (
              'Set up'
            )}
          </Button>
        )
      }
    />
  )

  if (editing) {
    return (
      <>
        <MailDialog
          organisation={organisation}
          saved={saved}
          onClose={rec.close}
          onSaved={async () => {
            await load()
            rec.close()
            flash('Mail server saved')
          }}
        />
        {toast}
      </>
    )
  }

  if (!loaded) {
    return (
      <>
        {head}
        {error && <Notice>{error}</Notice>}
      </>
    )
  }

  return (
    <>
      {head}
      <SettingsCard
        title="Mail server"
        aside={
          saved?.enabled ? (
            <StatusChip tone="ok">Sending</StatusChip>
          ) : (
            <StatusChip>{saved ? 'Not sending' : 'Not set up'}</StatusChip>
          )
        }
      >
        {!saved && <EmptyState icon={<Icon name="mail" size={20} />} title="No mail server yet" />}
        {saved?.passwordUnreadable && (
          <div className="connect-cardnote">
            <p className="connect-callout">
              <Icon name="alert" size={15} /> Stored password unreadable on this server. Enter it again.
            </p>
          </div>
        )}
        {saved && (
          <>
            <SettingRow label="Sender" description="Letters come from this address.">
              {saved.fromAddress || '—'}
            </SettingRow>
            <SettingRow label="Server" description="The relay letters leave through.">
              <Values
                items={[
                  { label: 'Address', value: <span className="connect-mono">{`${saved.host}:${saved.port}`}</span> },
                  { label: 'Encryption', value: sec?.name ?? saved.security },
                  { label: 'Username', value: saved.username || '—' },
                  { label: 'Password', value: saved.passwordSet ? <Masked /> : <span className="set-muted">Not stored</span> },
                  { label: 'Certificates', value: saved.caPem ? 'Own authority' : 'System' },
                ]}
              />
            </SettingRow>
            <SettingRow label="Allowed domains" description="Reports go only to these.">
              {(saved.allowedDomains ?? []).length > 0 ? (
                <TagList label="Allowed domains" items={(saved.allowedDomains ?? []).map((d) => ({ id: d, label: `@${d}` }))} />
              ) : (
                <span className="connect-warn">
                  <Icon name="alert" size={15} /> Any: reports can go anywhere.
                </span>
              )}
            </SettingRow>
          </>
        )}
      </SettingsCard>

      {saved && <TestLetter organisation={organisation} saved={saved} />}

      {toast}
    </>
  )
}

function MailDialog({
  organisation,
  saved,
  onClose,
  onSaved,
}: {
  organisation?: string
  saved: MailServerSettings | null
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState<MailServerDraft>(saved ? draftOf(saved) : BLANK)
  const [password, setPassword] = useState('')
  const [domain, setDomain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (patch: Partial<MailServerDraft>) => setDraft((d) => ({ ...d, ...patch }))

  const unsaved = mailFormChanged(saved, draft, draft.allowedDomains.join(', '), password)
  const portOk = Number.isInteger(draft.port) && draft.port >= 1 && draft.port <= 65535

  const addDomain = () => {
    // Commas or spaces between; a leading @ is how people write a domain and is not part of it.
    const typed = domainsFrom(domain.toLowerCase()).map((d) => d.replace(/^@/, '')).filter(Boolean)
    const next = [...draft.allowedDomains]
    for (const d of typed) if (!next.includes(d)) next.push(d)
    set({ allowedDomains: next })
    setDomain('')
  }

  async function save() {
    if (busy || !unsaved || !portOk) return
    setBusy(true)
    setError('')
    try {
      await api.saveMailServer({
        organisation,
        ...draft,
        host: draft.host.trim(),
        fromAddress: draft.fromAddress.trim(),
        username: draft.username.trim(),
        // Absent keeps the stored password; only a typed one replaces it.
        ...(password ? { password } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.mail')}
      title={saved ? 'Mail server' : 'Set up mail'}
      onClose={onClose}
      onSubmit={() => void save()}
      submitLabel="Save"
      valid={unsaved && portOk}
      busy={busy}
      dirty={unsaved}
    >
      {error && <Notice>{error}</Notice>}

      <section className="connect-part">
        <h3 className="connect-part__title">Sender</h3>
        <label className="field">
          <span>From address</span>
          <Input fullWidth value={draft.fromAddress} onChange={(e) => set({ fromAddress: e.target.value })} placeholder="qms@bank.az" />
        </label>
        <Toggle
          title="Send mail"
          hint="Off drops letters; none are sent later."
          checked={draft.enabled}
          onChange={(enabled) => set({ enabled })}
        />
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Mail server</h3>
        <div className="connect-port">
          <label className="field">
            <span>Server</span>
            <Input fullWidth value={draft.host} onChange={(e) => set({ host: e.target.value })} placeholder="relay.bank.az" />
          </label>
          <label className="field">
            <span>Port</span>
            <Input
              fullWidth
              type="number"
              min={1}
              max={65535}
              value={Number.isFinite(draft.port) ? draft.port : ''}
              onChange={(e) => set({ port: e.target.valueAsNumber })}
              aria-invalid={!portOk}
            />
          </label>
        </div>
        {!portOk && <p className="field-error">Port must be 1 to 65535.</p>}
        <fieldset className="field">
          <legend>Encryption</legend>
          <Seg
            label="Encryption"
            value={draft.security}
            // A port still at one of the usual three follows the choice; one typed by hand stays.
            onChange={(key) => {
              const s = SECURITY.find((x) => x.key === key) ?? SECURITY[0]
              set({ security: s.key, port: SECURITY.some((x) => x.port === draft.port) ? s.port : draft.port })
            }}
            options={SECURITY.map((s) => ({ value: s.key, label: s.name }))}
          />
        </fieldset>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Sign-in</h3>
        <label className="field">
          <span>Username</span>
          <Input
            fullWidth
            value={draft.username}
            onChange={(e) => set({ username: e.target.value })}
            placeholder="Empty if the relay trusts this server"
          />
        </label>
        {saved?.passwordUnreadable && (
          <Notice tone="warning">Stored password unreadable on this server. Enter it again.</Notice>
        )}
        <label className="field">
          <span>Password</span>
          <Input
            fullWidth
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={saved?.passwordSet ? 'Stored. Leave empty to keep it.' : 'Not stored'}
          />
        </label>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Allowed domains</h3>
        <TagList
          label="Allowed domains"
          items={draft.allowedDomains.map((d) => ({ id: d, label: `@${d}` }))}
          onRemove={(d) => set({ allowedDomains: draft.allowedDomains.filter((x) => x !== d) })}
        />
        <div className="connect-chip-add">
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="bank.az"
            aria-label="Add a domain"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                addDomain()
              }
            }}
          />
          <Button variant="secondary" isDisabled={!domain.trim()} onPress={addDomain}>
            <Icon name="plus" size={14} /> Add
          </Button>
        </div>
        {draft.allowedDomains.length === 0 && (
          <p className="connect-warn">
            <Icon name="alert" size={15} /> No domain: reports can go anywhere.
          </p>
        )}
        <details className="connect-more" open={draft.caPem !== ''}>
          <summary title="PEM, only for the organisation's own authority.">Certificate authority</summary>
          <TextArea
            fullWidth
            className="connect-textarea"
            rows={4}
            value={draft.caPem}
            onChange={(e) => set({ caPem: e.target.value })}
            placeholder="-----BEGIN CERTIFICATE-----"
          />
        </details>
      </section>
    </InnerPage>
  )
}

function TestLetter({ organisation, saved }: { organisation?: string; saved: MailServerSettings }) {
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<{ to: string; outside: boolean } | null>(null)

  async function send(event: FormEvent) {
    event.preventDefault()
    const addr = to.trim()
    setBusy(true)
    setError('')
    setSent(null)
    try {
      const { outsideAllowedDomains } = await api.testMailServer(organisation, addr)
      setSent({ to: addr, outside: Boolean(outsideAllowedDomains) })
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard title="Test letter" line="Uses the saved settings.">
      <SettingRow as="form" onSubmit={send} label="Send to">
        <span className="connect-trial">
          <Input
            type="email"
            aria-label="Send to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@bank.az"
          />
          <Button type="submit" variant="primary" isDisabled={busy || !to.trim()}>
            <Mark name="send" size={16} /> {busy ? 'Sending…' : 'Send test'}
          </Button>
        </span>
      </SettingRow>
      {(error || sent) && (
        <SettingBlock>
          {error && (
            <TestResult ok={false} title="Not sent">
              <span>{error}</span>
            </TestResult>
          )}
          {sent && (
            <TestResult ok title="Accepted">
              <span>
                {saved.host} accepted it for {sent.to}.
              </span>
              {sent.outside && <span>Outside the allowed domains: reports never go there.</span>}
            </TestResult>
          )}
        </SettingBlock>
      )}
    </SettingsCard>
  )
}
